// ============================================
// CoverImageTemplateBrowser.js
// Modal for browsing and selecting cover image templates
// Updated for WebP support
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import './CoverImageTemplateBrowser.css';

const CoverImageTemplateBrowser = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  coverType = 'invitation', // 'invitation' or 'greeting'
  supabaseClient // Pass your Supabase client instance
}) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Event types list
  const EVENT_TYPES = [
    'Anniversary',
    'Annual Function',
    'Art Exhibition',
    'Baby Naming Ceremony',
    'Baby Shower',
    'BBQ',
    'Beach Party',
    'Birthday Party',
    'Book Club Meetup',
    'Bridal Shower',
    'Camping',
    'Charity Event',
    'Christmas Party',
    'Cocktail Party',
    'Community Gathering',
    'Conference',
    'Concert',
    'Cultural Event',
    'Diwali',
    'Dinner Party',
    'Eid',
    'Engagement Party',
    'Farewell Party',
    'Festival',
    'Festival Afterparty',
    'Freshers Party',
    'Fundraiser',
    'Game Night',
    'Graduation',
    'Haldi',
    'Halloween Party',
    'Hiking',
    'Holiday Celebration',
    'Holi',
    'Housewarming',
    'Jagraata',
    'Katha',
    'Karaoke Night',
    'Launch Party',
    'Meet & Greet',
    'Mehendi',
    'Movie Night',
    'Music Night',
    'Networking Event',
    'New Year Party',
    'Office Party',
    'Open Mic',
    'Paath',
    'Photo Walk',
    'Picnic',
    'Pool Party',
    'Pooja',
    'Potluck',
    'Reunion',
    'Religious Ceremony',
    'Road Trip',
    'Sangeet',
    'Seminar',
    'Sports Viewing',
    'Startup Pitch Event',
    'Wedding',
    'Workshop',
    'Wellness Session',
    'Yoga Session',
  ];

  // Load templates
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseClient
        .from('cover_image_templates')
        .select('*')
        .eq('cover_type', coverType)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      alert('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [coverType, supabaseClient]);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, coverType, loadTemplates]);

  // Filter templates
  const filterTemplates = useCallback(() => {
    let filtered = [...templates];

    // Filter by event type
    if (selectedEventType) {
      filtered = filtered.filter((t) => t.event_type === selectedEventType);
    }

    // Filter by search query (keywords or event type)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((t) => {
        const matchesEventType = t.event_type.toLowerCase().includes(query);
        const matchesKeywords = t.keywords?.some((k) =>
          k.toLowerCase().includes(query)
        );
        return matchesEventType || matchesKeywords;
      });
    }

    setFilteredTemplates(filtered);
  }, [templates, selectedEventType, searchQuery]);

  useEffect(() => {
    filterTemplates();
  }, [templates, selectedEventType, searchQuery, filterTemplates]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleConfirmSelection = () => {
    if (selectedTemplate) {
      onSelect(selectedTemplate);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setSearchQuery('');
    setSelectedEventType('');
    onClose();
  };

  const handleClearFilter = () => {
    setSelectedEventType('');
    setSearchQuery('');
  };

  if (!isOpen) return null;

  return (
    <div className="template-browser-overlay" onClick={handleClose}>
      <div className="template-browser-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="browser-header">
          <div className="header-left">
            <h2>
              {coverType === 'invitation' ? '📨 Invitation' : '💌 Greeting Card'} Templates
            </h2>
            <p>{filteredTemplates.length} templates available</p>
          </div>
          <button className="close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        {/* Filters */}
        <div className="browser-filters">
          {/* Search */}
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by event type or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search" onClick={() => setSearchQuery('')}>
                ×
              </button>
            )}
          </div>

          {/* Event Type Filter */}
          <div className="filter-group">
            <select
              value={selectedEventType}
              onChange={(e) => setSelectedEventType(e.target.value)}
            >
              <option value="">All Event Types</option>
              {EVENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {(selectedEventType || searchQuery) && (
            <button className="clear-filters-btn" onClick={handleClearFilter}>
              Clear Filters
            </button>
          )}
        </div>

        {/* Templates Grid */}
        <div className="templates-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="empty-state">
              <p>😔 No templates found</p>
              <p>Try adjusting your filters or search query</p>
            </div>
          ) : (
            <div className="templates-grid">
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplate?.id === template.id}
                  onSelect={handleSelectTemplate}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="browser-footer">
          <button className="cancel-btn" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="select-btn"
            onClick={handleConfirmSelection}
            disabled={!selectedTemplate}
          >
            {selectedTemplate ? 'Use This Template' : 'Select a Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Template Card Component
const TemplateCard = ({ template, isSelected, onSelect }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Get file sizes for display
  const getFileSizes = () => {
    const sizes = [];
    
    if (template.webp_size) {
      sizes.push(`WebP: ${(template.webp_size / 1024).toFixed(0)}KB`);
    }
    
    if (template.file_size) {
      const compression = template.webp_size 
        ? ((1 - (template.webp_size / template.file_size)) * 100).toFixed(0)
        : 0;
      sizes.push(`PNG: ${(template.file_size / 1024).toFixed(0)}KB`);
      if (compression > 0) {
        sizes.push(`${compression}% smaller`);
      }
    }
    
    return sizes.join(' • ');
  };

  return (
    <div
      className={`template-card ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(template)}
    >
      {/* Image - Use WebP thumbnail for browsing */}
      <div className="template-image">
        {!imageLoaded && (
          <div className="image-skeleton">
            <div className="spinner"></div>
          </div>
        )}
        <img
          src={template.thumbnail_url}
          alt={template.event_type}
          onLoad={() => setImageLoaded(true)}
          style={{ display: imageLoaded ? 'block' : 'none' }}
        />
        {isSelected && (
          <div className="selected-overlay">
            <div className="checkmark">✓</div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="template-info">
        <h4>{template.event_type}</h4>
        
        {template.keywords && template.keywords.length > 0 && (
          <div className="template-keywords">
            {template.keywords.slice(0, 3).map((keyword, idx) => (
              <span key={idx} className="keyword-badge">
                {keyword}
              </span>
            ))}
            {template.keywords.length > 3 && (
              <span className="keyword-badge more">
                +{template.keywords.length - 3}
              </span>
            )}
          </div>
        )}
        
        <div className="template-meta">
          {template.width && template.height && (
            <div className="dimensions">
              {template.width} × {template.height}
            </div>
          )}
          {(template.webp_size || template.file_size) && (
            <div className="file-sizes" title={getFileSizes()}>
              {getFileSizes()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoverImageTemplateBrowser;